import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router'
import { App } from 'antd'
import { resubmitApplicationSchema, type ResubmitApplicationInput } from '@super-mega/server/contracts/auth'
import { AccountStatusPanel, AuthShell, getAuthError } from '@/features/auth/index.ts'
import { AUTH_SESSION_STALE_TIME } from '@/trpc/context.ts'
import { useTRPC } from '@/trpc/react.ts'

export const Route = createFileRoute('/auth/status')({
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.fetchQuery({
      ...context.trpc.auth.session.queryOptions(),
      retry: false,
      staleTime: AUTH_SESSION_STALE_TIME
    })
    if (!session) {
      throw redirect({ to: '/auth/signin', replace: true })
    }
    if (session.status === 'active' && session.member) {
      throw redirect({ to: '/home', replace: true })
    }
  },
  component: AccountStatusPage,
  head: () => ({
    meta: [{ title: '账号状态 · Super Mega' }]
  })
})

function AccountStatusPage() {
  const trpc = useTRPC()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { message } = App.useApp()
  const sessionQuery = useQuery({
    ...trpc.auth.session.queryOptions(),
    refetchInterval: 30_000,
    staleTime: 0
  })
  const resubmit = useMutation(trpc.auth.resubmitApplication.mutationOptions())
  const logout = useMutation(trpc.auth.logout.mutationOptions())
  const session = sessionQuery.data

  useEffect(() => {
    if (sessionQuery.isSuccess && !session) {
      void router.navigate({ to: '/auth/signin', replace: true })
      return
    }
    if (session?.status === 'active' && session.member) {
      void router.navigate({ to: '/home', replace: true })
    }
  }, [router, session, sessionQuery.isSuccess])

  const handleLogout = async () => {
    try {
      await logout.mutateAsync()
      await queryClient.cancelQueries()
      queryClient.clear()
      await router.navigate({ to: '/auth/signin', replace: true })
    } catch (error) {
      void message.error(getAuthError(error, '退出').message)
    }
  }

  const handleResubmit = async (input: ResubmitApplicationInput) => {
    try {
      const parsed = resubmitApplicationSchema.parse(input)
      const next = await resubmit.mutateAsync(parsed)
      queryClient.setQueryData(trpc.auth.session.queryKey(), next)
      void message.success('申请已重新提交')
    } catch (error) {
      void message.error(getAuthError(error, '重新提交申请').message)
    }
  }

  if (!session) {
    return null
  }

  return (
    <AuthShell>
      <AccountStatusPanel
        loggingOut={logout.isPending}
        onLogout={() => void handleLogout()}
        onRefresh={() => void sessionQuery.refetch()}
        onResubmit={(values) => void handleResubmit(values)}
        refreshError={sessionQuery.isError}
        refreshing={sessionQuery.isFetching}
        session={session}
        submitting={resubmit.isPending}
      />
    </AuthShell>
  )
}
